import React from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, ScoreChip, SectionTitle, EmptyState } from "@/components/common";
import { fmtUsd, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { Radio, Trash2, Info } from "lucide-react";

const DISCOVERY = [
  ["most_watched", "Most Watched"],
  ["most_active", "Most Active"],
  ["early_buyers", "Most Early"],
  ["consistent", "Most Consistent"],
  ["top_pnl", "Top PnL"],
  ["low_mc_entries", "Top Low-MC Entries"],
];

export default function WalletTracker() {
  const nav = useNavigate();
  const { data: tracked, reload } = useFetch("/tracked-wallets", []);
  const [board, setBoard] = React.useState("most_active");
  const { data: disc } = useFetch(`/wallets?board=${board}&limit=12`, [board]);

  const untrack = async (id) => {
    await http.delete(`/tracked-wallets/${id}`);
    toast.success("Removed from tracker");
    reload();
  };
  const track = async (w) => {
    await http.post("/tracked-wallets", { kind: "wallet", ref_id: w.address, label: w.short, sub: w.category });
    toast.success("Tracking wallet");
    reload();
  };

  return (
    <div className="fade-in">
      <PageHeader icon={Radio} title="Wallet Tracker" subtitle="Follow smart-money and suspicious wallets. Tracking popularity is shown separately from performance." />

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-cyan-800/50 bg-cyan-950/20 px-4 py-2.5 text-xs text-cyan-300">
        <Info className="h-4 w-4" /> Tracking a wallet does NOT imply it performs well. It only means you want to monitor its activity.
      </div>

      <SectionTitle right={<span className="live-dot" />}>Tracked Wallets ({tracked?.data?.length || 0})</SectionTitle>
      {!tracked ? <Loading /> : tracked.data.length === 0 ? (
        <EmptyState title="No wallets tracked yet" hint="Add wallets from any leaderboard, wallet profile, or the discovery lists below." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tracked.data.map((w) => (
            <div key={w.id} className="pnut-card flex items-center justify-between p-4">
              <button onClick={() => nav(`/wallet/${w.ref_id}`)} className="text-left">
                <div className="font-mono text-sm text-slate-200">{w.label}</div>
                <div className="text-[11px] text-slate-500">{w.sub}</div>
              </button>
              <button data-testid={`untrack-${w.id}`} onClick={() => untrack(w.id)} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-950/40 hover:text-rose-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <SectionTitle right={
          <div className="flex flex-wrap gap-1.5">
            {DISCOVERY.map(([v, l]) => (
              <button key={v} onClick={() => setBoard(v)} className={`rounded-md border px-2 py-1 text-[11px] ${board === v ? "border-amber-600/50 text-amber-300" : "border-slate-800 text-slate-500"}`}>{l}</button>
            ))}
          </div>
        }>Discover Wallets</SectionTitle>
        {!disc ? <Loading /> : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {disc.data.map((w) => (
              <div key={w.address} className="pnut-card p-4">
                <div className="flex items-start justify-between">
                  <button onClick={() => nav(`/wallet/${w.address}`)} className="text-left">
                    <div className="font-mono text-sm text-slate-200">{w.short}</div>
                    <div className="text-[11px] text-slate-500">{w.category}</div>
                  </button>
                  <ScoreChip type="momentum" value={w.smart_score} />
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Early {w.early_entry_rate}% · {fmtNum(w.watchers_count)} watchers</span>
                  <button onClick={() => track(w)} className="rounded-md border border-amber-600/40 px-2 py-1 text-[10px] font-semibold text-amber-300 hover:bg-amber-500/10">Track</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
